// Candidate document-upload pages: the per-tenant "Save changes" buttons of the basic page
// (resources/views/account/upload-documents.blade.php) and the "Send my documents to the
// landlord" button, which only the property-related page
// (resources/views/account/upload-documents-property.blade.php) renders, are full server-side
// form submits that end in AccountController::tenantUpload()'s `redirect()->back()`. A URL fragment
// cannot survive that round trip - browsers never send the fragment in the redirected request,
// so `back()` always lands on the bare page URL. sessionStorage is the only way this flow can
// hand the next page load "which section was open" - it lives in this browser tab only, so it is
// automatically unaffected by locale prefix, query string, or selected website.
//
// The restore target is the SPECIFIC element the candidate submitted from (a stable id given to
// each per-tenant "Save changes" button - `documents-save-{tenantId}` - and to the property
// page's single "Send my documents to the landlord" section - `documents-final-section`; both
// built from ids already rendered in the page, never a text/index match), falling back to the top of the active tenant
// tab (`#tab-{tenantId}`) when that element can't be found (e.g. the tab layout changed between
// submit and reload). document.activeElement is NOT used to find this element: Safari/iOS does
// not reliably focus a clicked <button>/<input type=submit>, so relying on it silently drops the
// restore on exactly the platforms this page must support. Callers pass the submitting element
// explicitly instead (the native SubmitEvent's `.submitter`, or the button a click handler
// already has a reference to).
//
// Keys are namespaced by subscription id so a stored value can only ever be read back by that
// same subscription's own page load - a different candidate (a different subscription id) or a
// later, unrelated dossier can never pick up someone else's saved section.

/**
 * @param {string|number} subscriptionId
 * @returns {string}
 */
export function tabStorageKey(subscriptionId) {
  return 'uploadDocumentsActiveTab:' + subscriptionId;
}

/**
 * @param {string|number} subscriptionId
 * @returns {string}
 */
export function elementStorageKey(subscriptionId) {
  return 'uploadDocumentsActiveElement:' + subscriptionId;
}

/**
 * @param {Document} doc
 * @returns {string|null} the tenant id portion of the currently active tab, e.g. "42"
 */
export function getActiveTabId(doc) {
  var activeLink = doc.querySelector('[data-tab].active');
  return activeLink ? activeLink.getAttribute('data-tab').replace('tab-', '') : null;
}

/**
 * Call immediately before ANY full-page submit on these pages (the native "Save changes"
 * submit and the property page's JS-driven "Send my documents to the landlord" submit) so the
 * next page load can return to the same section. A no-op when there is no tab UI at all (a single-tenant
 * subscription renders no tabs) or nothing is currently marked active.
 *
 * @param {string|number} subscriptionId
 * @param {Document} doc
 * @param {Storage} storage
 * @param {Element|null} submittingElement the button/control that triggered this submit, if
 *   known - e.g. the native SubmitEvent's `.submitter`, or a click handler's own `this`. Only
 *   remembered when it carries an id (both restore-eligible buttons on this page always do).
 */
export function rememberActiveSection(subscriptionId, doc, storage, submittingElement) {
  var tabId = getActiveTabId(doc);
  if (tabId) {
    storage.setItem(tabStorageKey(subscriptionId), tabId);
  }
  if (submittingElement && submittingElement.id) {
    storage.setItem(elementStorageKey(subscriptionId), submittingElement.id);
  }
}

/**
 * Decides what to restore on page load, consuming any stored value exactly once so a later
 * plain visit/refresh never reuses it (and never leaks into a different subscription's page,
 * since the keys read are namespaced to `subscriptionId`). An explicit `#tabhref-N` link always
 * wins over a stored value - a user who deliberately followed a link to a specific tab must land
 * there, not wherever they last submitted from.
 *
 * @param {string|number} subscriptionId
 * @param {Storage} storage
 * @param {string} hash window.location.hash
 * @returns {{tabId: (string|null), elementId: (string|null), fromSession: boolean}}
 */
export function consumeRestoreTarget(subscriptionId, storage, hash) {
  if (hash && hash.indexOf('tabhref') !== -1) {
    return { tabId: hash.split('-')[1], elementId: null, fromSession: false };
  }
  var tabKey = tabStorageKey(subscriptionId);
  var elementKey = elementStorageKey(subscriptionId);
  var savedTab = storage.getItem(tabKey);
  var savedElement = storage.getItem(elementKey);
  storage.removeItem(tabKey);
  storage.removeItem(elementKey);

  return { tabId: savedTab || null, elementId: savedElement || null, fromSession: !!savedTab };
}

/**
 * Marks the given tab id active, falling back to the first tab when the id doesn't match any
 * (e.g. a stale id from a tenant that was since removed). Returns the id actually activated, or
 * null if the page has no tabs at all.
 *
 * @param {string|null} tabId
 * @param {Document} doc
 * @returns {string|null}
 */
export function activateTab(tabId, doc) {
  var links = doc.querySelectorAll('a[data-tab]');
  if (!links.length) {
    return null;
  }
  if (tabId) {
    var link = doc.querySelector('[data-tab=tab-' + tabId + ']');
    var tab = doc.getElementById('tab-' + tabId);
    if (link && tab) {
      link.classList.add('active');
      tab.classList.add('active');
      return tabId;
    }
  }
  var firstLink = links[0];
  var firstTab = doc.querySelector('.document-tabs__tab');
  firstLink.classList.add('active');
  if (firstTab) {
    firstTab.classList.add('active');
  }
  return firstLink.getAttribute('data-tab').replace('tab-', '');
}

/**
 * Height, in pixels, of a fixed/sticky element the browser's own scrollIntoView() does not know
 * to avoid (it would otherwise slide the target section's top edge underneath it). Measures the
 * public layout's `.page__header` - the sticky wrapper around `.header`
 * (resources/sass/blocks/_header.scss). `.header` itself stays `position: relative`, so checking
 * it would always report 0 and let the restored section land underneath the sticky menu.
 *
 * @param {Document} doc
 * @param {Window} win
 * @returns {number}
 */
export function computeFixedHeaderOffset(doc, win) {
  var header = doc.querySelector('.page__header');
  if (!header) {
    return 0;
  }
  var position = win.getComputedStyle(header).position;
  if (position === 'fixed' || position === 'sticky') {
    return header.getBoundingClientRect().height;
  }
  return 0;
}

/**
 * Scrolls the given target into view - the exact button/element the candidate submitted from
 * when `elementId` resolves, otherwise the top of its tab's section - clearing any fixed header
 * and respecting prefers-reduced-motion. Returns false (and does nothing) if neither id resolves
 * to an element.
 *
 * @param {string|null} elementId
 * @param {string} tabId
 * @param {Document} doc
 * @param {Window} win
 * @returns {boolean}
 */
export function scrollToSection(elementId, tabId, doc, win) {
  var target = (elementId && doc.getElementById(elementId)) || doc.getElementById('tab-' + tabId);
  if (!target) {
    return false;
  }
  var reducedMotion = !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var offset = computeFixedHeaderOffset(doc, win);
  if (offset > 0) {
    var top = target.getBoundingClientRect().top + win.pageYOffset - offset - 12;
    win.scrollTo({ top: top < 0 ? 0 : top, behavior: reducedMotion ? 'auto' : 'smooth' });
  } else {
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }
  return true;
}

export default {
  tabStorageKey: tabStorageKey,
  elementStorageKey: elementStorageKey,
  getActiveTabId: getActiveTabId,
  rememberActiveSection: rememberActiveSection,
  consumeRestoreTarget: consumeRestoreTarget,
  activateTab: activateTab,
  computeFixedHeaderOffset: computeFixedHeaderOffset,
  scrollToSection: scrollToSection,
};
